import { useEffect, useRef } from 'react';
import type { Track } from 'livekit-client';

/**
 * A LiveKit video track (the peer's camera, or this side's own) rendered into
 * a <video> owned by React. The track is attached on mount and detached from
 * this element only on unmount, so the provider never touches DOM it does
 * not own; unmounting is also what lets adaptiveStream pause a hidden feed.
 */
export function VideoTrackView({ track, className, mirrored = false }: {
  track: Track;
  className?: string;
  /** Self-view: mirrored like every camera app's preview. */
  mirrored?: boolean;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    track.attach(element);
    return () => {
      try { track.detach(element); } catch { /* already detached */ }
    };
  }, [track]);
  return (
    <video
      ref={ref}
      className={`${className ?? ''}${mirrored ? ' cs-video--mirrored' : ''}`.trim()}
      autoPlay
      playsInline
      muted
      aria-hidden="true"
      data-testid="call-video"
    />
  );
}

// ── Inline icons (stroke = currentColor), shared by the 1:1 and group UIs ──
export const VideoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);
export const VideoOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

/** The camera toggle, in the same control-column shape as Mute. */
export function CameraControl({ on, busy = false, onToggle }: {
  on: boolean;
  busy?: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="cs-control">
      <button
        type="button"
        className={`cs-btn${on ? ' cs-btn--muted' : ''}`}
        onClick={onToggle}
        disabled={busy}
        aria-pressed={on}
        aria-label={on ? 'Turn off camera' : 'Turn on camera'}
      >
        {on ? <VideoIcon /> : <VideoOffIcon />}
      </button>
      <span className="cs-control-label">{on ? 'Video off' : 'Video'}</span>
    </div>
  );
}
